/**
 * Sandhi: joining and, more usefully, splitting.
 *
 * Sanskrit is written with words fused together, which is the first obstacle a
 * reader meets. A text reads *tacchrutvā*, and no dictionary has that headword;
 * what the reader wants is *tat* + *śrutvā*.
 *
 * The engine ships a sandhi table of ~1,468 rules inside the .wasm we already
 * load, so this costs nothing to add. It is *generative*, though: asked to split
 * *tacchrutvā* it offers eighteen possibilities, of which one is right and the
 * rest are things like *t* + *acchrutvā*. Splitting alone is not an answer.
 *
 * So the same discipline is applied here as for reverse declension: propose
 * cheaply, then keep only what survives a check. `splitWord` returns the
 * candidates; the caller decides which halves are real words, because only the
 * caller has the dictionary.
 */

import { iastToSlp1, loadEngine, normalizeIast, slp1ToIast } from "./vidyut-engine";

export type SandhiSplit = {
    /** The first word, in IAST. */
    first: string;
    /** The remainder, in IAST. */
    second: string;
};

type SandhiEngine = {
    splitAll(input: string): Array<{
        first: string; second: string; isValid: boolean; isEndOfChunk: boolean; kind: string;
    }>;
    join(first: string, second: string): string;
    rules(): unknown[];
    free(): void;
};

let sandhi: SandhiEngine | null = null;
let pending: Promise<SandhiEngine | null> | null = null;

/** Loads the sandhi table, once. Shares the .wasm with the grammar engine. */
export const loadSandhi = (): Promise<SandhiEngine | null> => {
    if (sandhi) return Promise.resolve(sandhi);
    if (pending) return pending;

    pending = (async () => {
        try {
            // Ensures the module is initialised; the two share one .wasm.
            await loadEngine();
            const vidyut: any = await import("@siva-sh/vidyut");
            sandhi = new vidyut.Sandhi() as SandhiEngine;
            return sandhi;
        } catch (e) {
            console.error("vidyut: failed to load sandhi rules", e);
            return null;
        } finally {
            // A failed load must not be remembered; see vidyut-engine.ts.
            if (!sandhi) pending = null;
        }
    })();

    return pending;
};

/**
 * Every way the text could be split, longest first word first.
 *
 * These are candidates, not answers. A single word yields on the order of ten
 * to twenty, nearly all of them nonsense — the caller must decide which halves
 * are real. Splits with a one-character half are dropped, since those are the
 * bulk of the noise and almost never what a reader wants.
 */
export const splitWord = async (text: string): Promise<SandhiSplit[]> => {
    const engine = await loadSandhi();
    if (!engine) return [];

    const clean = normalizeIast(text.trim().toLowerCase());
    if (clean.length < 3) return [];

    let raw;
    try {
        raw = engine.splitAll(iastToSlp1(clean));
    } catch (e) {
        console.warn("vidyut: sandhi split failed", text, e);
        return [];
    }

    const seen = new Set<string>();
    const out: SandhiSplit[] = [];
    for (const s of raw) {
        if (!s.isValid) continue;
        const first = slp1ToIast(s.first);
        const second = slp1ToIast(s.second);
        if (first.length < 2 || second.length < 2) continue;
        const key = `${first}|${second}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ first, second });
    }

    // A longer first word is usually the better reading, and it puts the
    // plausible candidates in front of whatever budget the caller has.
    return out.sort((a, b) => b.first.length - a.first.length);
};

/** Joins two words as sandhi requires: tat + eva becomes *tad eva*. */
export const joinWords = async (first: string, second: string): Promise<string | null> => {
    const engine = await loadSandhi();
    if (!engine) return null;
    try {
        return slp1ToIast(engine.join(iastToSlp1(first), iastToSlp1(second)));
    } catch {
        return null;
    }
};
